import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BaseChartDirective } from 'ng2-charts';
import { ActiveElement, ChartData, ChartEvent, ChartOptions } from 'chart.js';
import { CardComponent } from '@custom-components/general/card';
import { withAlpha } from '@custom-components/general/chart/chart.component';

export type AlarmPriority = 'High' | 'Medium' | 'Low';

export interface AlarmPriorityWedge {
  priority: AlarmPriority;
  color: string;
  label: string;
  count: number;
}

export interface AlarmTypeBar {
  group: string;
  label: string;
  count: number;
  segments: { priority: AlarmPriority; count: number; color: string }[];
}

@Component({
  selector: 'xemex-dashboard-alarms-widget',
  imports: [CardComponent, TranslateModule, BaseChartDirective],
  styleUrl: './dashboard-alarms-widget.component.scss',
  templateUrl: './dashboard-alarms-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardAlarmsWidgetComponent {
  total = input.required<number>();
  priorityWedges = input.required<AlarmPriorityWedge[]>();
  byType = input.required<AlarmTypeBar[]>();
  maxTypeCount = input.required<number>();

  viewAll = output<void>();
  priorityFilterClick = output<AlarmPriority>();
  typeFilterClick = output<string>();

  /** Hovered priority wedge/legend row, `null` when nothing is hovered — same dim-the-rest
   * pattern as the devices widget's donut, kept local since it's purely visual. */
  protected readonly hoveredPriority = signal<AlarmPriority | null>(null);

  private readonly baseChart = viewChild(BaseChartDirective);

  // A `pie` chart is Chart.js's `doughnut` controller with `cutout: 0` — same controller
  // `xemex-donut-chart` wraps, but this widget owns its own canvas rather than reusing that
  // component because clicks/hovers here key off the raw `AlarmPriority` union, not an
  // arbitrary translated segment label.
  //
  // Base colors only, deliberately NOT reactive to `hoveredPriority` — feeding hover state
  // through this `[data]`-bound signal would hand Chart.js a brand-new `data` object on every
  // hover, and ng2-charts applies that via a full `chart.update()`, which replays the pie's
  // default grow-in (`animateRotate`) animation on every hover. The hover-driven recolor below
  // applies imperatively instead, via `chart.update('none')` — the same no-animation update
  // `ChartSyncService` uses for cross-chart hover sync.
  protected readonly chartData = computed<ChartData<'pie'>>(() => ({
    labels: this.priorityWedges().map(w => w.priority),
    datasets: [
      {
        data: this.priorityWedges().map(w => w.count),
        backgroundColor: this.priorityWedges().map(w => w.color),
        hoverBackgroundColor: this.priorityWedges().map(w => w.color),
        borderWidth: 0,
        hoverBorderWidth: 0,
      },
    ],
  }));

  protected readonly chartOptions: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
  };

  constructor() {
    effect(() => {
      const hovered = this.hoveredPriority();
      const wedges = this.priorityWedges();
      const chart = this.baseChart()?.chart;
      if (!chart) return;
      const colors = wedges.map(w =>
        hovered && hovered !== w.priority ? withAlpha(w.color, 0.55) : w.color,
      );
      const dataset = chart.data.datasets[0];
      dataset.backgroundColor = colors;
      dataset.hoverBackgroundColor = colors;
      chart.update('none');
    });
  }

  protected onChartHover({ active }: { event: ChartEvent; active: object[] }): void {
    const index = (active as ActiveElement[])[0]?.index;
    this.hoveredPriority.set(index != null ? this.priorityWedges()[index].priority : null);
  }

  protected onChartClick({ active }: { event?: ChartEvent; active?: object[] }): void {
    const index = (active as ActiveElement[] | undefined)?.[0]?.index;
    if (index != null) this.priorityFilterClick.emit(this.priorityWedges()[index].priority);
  }
}
